import { useCallback } from 'react';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useStorageValue } from '../../../../hooks/useStorageValue';

const STORAGE_KEY = 'ua_isLowEndDevice';
const STORAGE_VERSION = 1;

interface StoredFormat {
  v: number;
  value: boolean;
}

function isStoredFormat(data: unknown): data is StoredFormat {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as StoredFormat).v === 'number' &&
    typeof (data as StoredFormat).value === 'boolean'
  );
}

/**
 * Deserializes stored value, supporting both versioned JSON format and legacy plain string format.
 */
function deserializeStoredValue(stored: string | undefined): boolean | undefined {
  if (!stored) return undefined;

  // Try versioned JSON format first
  try {
    const parsed: unknown = JSON.parse(stored);
    if (isStoredFormat(parsed) && parsed.v === STORAGE_VERSION) {
      return parsed.value;
    }
  } catch {
    // Not valid JSON, fall back to legacy format
  }

  // Legacy format: plain boolean strings ('true' or 'false')
  if (stored === 'true') return true;
  if (stored === 'false') return false;

  return undefined;
}

/**
 * Serializes boolean value to versioned JSON format for storage.
 */
function serializeStoredValue(value: boolean): string {
  const payload: StoredFormat = {
    v: STORAGE_VERSION,
    value,
  };
  return JSON.stringify(payload);
}

/**
 * Hook for reading and writing isLowEndDevice value to/from localStorage.
 * Provides reactive access to device capability detection results persisted across sessions.
 */
export const useIsLowEndDeviceFromLocalStorage = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const [setItem, getItem] = useLocalStorage<string>(STORAGE_KEY, undefined, {
    raw: true,
  });

  const getItemParsed = useCallback(async () => {
    const stored = await getItem();
    return deserializeStoredValue(stored);
  }, [getItem]);

  const setItemParsed = useCallback(
    async (value: boolean | undefined) => {
      if (value === undefined) {
        return;
      }
      try {
        const serialized = serializeStoredValue(value);
        await setItem(serialized);
      } catch (error) {
        // Handle localStorage-specific errors (quota exceeded, access denied)
        if (error instanceof DOMException) {
          const message =
            error.name === 'QuotaExceededError'
              ? 'localStorage quota exceeded'
              : `localStorage access denied: ${error.name}`;
          console.warn(`[DeviceCapabilities] ${message}`);
        }
        throw error;
      }
    },
    [setItem],
  );

  const {
    data: value,
    isLoading: loading,
    setStorageValue,
  } = useStorageValue<boolean | undefined>({
    getItem: getItemParsed,
    setItem: setItemParsed,
    enabled,
  });

  const setValue = useCallback(
    (isLowEnd: boolean) => {
      void setStorageValue(isLowEnd);
    },
    [setStorageValue],
  );

  return {
    value,
    loading,
    setValue,
  };
};
